import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { postLoginPath } from '@/lib/roleLanding'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Eye, EyeOff } from 'lucide-react'
import { PRODUCT, PRODUCT_KEY } from '@/lib/productIdentity'
import { useSignInBrand } from '@/brand/useSignInBrand'

// VALIDATION-1B: format check only — deliberately permissive about what a local
// part may contain, because the only thing worth blocking here is a typo the
// user can see for themselves (missing @, missing domain, trailing comma).
// Anything stricter rejects legitimate firm addresses, and the server is the
// authority on whether the account exists regardless.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// PLAT-PROD-GUARDS-1 G2: OWNER TEXT PENDING — not owner-approved yet. Kept as a
// single key so the approved wording replaces it in one place.
const CLIENT_TEXT = {
  'signin.dbWaking': "We're getting things ready. Signing you in automatically in a moment…",
} as const

// The first sign-in after the serverless database has auto-paused gets 503
// {"code": "DB_WAKING"} while it resumes (up to ~1 minute). Re-send the same
// request every 10 s, at most 9 times (90 s), then show the normal error.
const DB_WAKING_RETRY_MS = 10_000
const DB_WAKING_MAX_RETRIES = 9

function isDbWaking(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: string } } })?.response
  return res?.status === 503 && res?.data?.code === 'DB_WAKING'
}

export function Login() {
  const { login, isAuthenticated, user } = useAuth()
  // PLAT-AUTH-UI-ONE-3 (H1): null = the default Yansar brand, rendered exactly
  // as before. A white-label brand (Architecture Rule 5) gets its own logo or
  // initial, colours, name, footer and contact line, and no Yansar value.
  const wl = useSignInBrand()
  const navy = wl?.primaryColor || '#0d1f3c'
  const gold = wl?.accentColor || '#c9a227'
  const [logoFailed, setLogoFailed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  // ProtectedRoute stashes the blocked location here so a deep link survives login.
  const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from ?? null

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [showEmailForm, setShowEmailForm] = useState(false)
  // PLAT-SIGNIN-POLISH-1: Google is offered only where the server has it set up.
  // Any failure to find out is treated as "not set up".
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const [ssoChecked, setSsoChecked] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [dbWaking, setDbWaking] = useState(false)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // A pending DB_WAKING retry must not fire after the page is gone.
  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current)
  }, [])

  // Redirect if already authenticated. postLoginPath resolves
  // forced-password-change > preserved deep link > role landing, so a CPA with
  // no deep link reliably lands on /dashboard rather than bouncing through '/'.
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(postLoginPath(user, from), { replace: true })
    }
  }, [isAuthenticated, user, navigate, from])

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // VALIDATION-1B: both fields are checked before the request goes out, and
    // both messages show at once — validating one field at a time makes the user
    // submit twice to learn they got two things wrong.
    const trimmedEmail = email.trim()
    const nextEmailError = EMAIL_RE.test(trimmedEmail) ? null : 'Enter a valid email address.'
    // Presence ONLY. The password's content is never inspected, measured, or
    // reported on — a client-side strength or length rule here would leak the
    // server's policy and tell an attacker when they have a well-formed guess.
    const nextPasswordError = password === '' ? 'Enter your password.' : null
    setEmailError(nextEmailError)
    setPasswordError(nextPasswordError)
    if (nextEmailError || nextPasswordError) return

    if (retryTimer.current) clearTimeout(retryTimer.current)
    retryTimer.current = null
    setLoading(true)
    await attemptLogin({ email: trimmedEmail, password }, 0)
  }

  // One sign-in request. On DB_WAKING it schedules the identical request again
  // (loading stays on, the typed password is untouched); every other outcome
  // ends the attempt. A wrong password (401) is never retried.
  const attemptLogin = async (creds: { email: string; password: string }, retry: number) => {
    try {
      await login(creds)
      // Navigation handled by useEffect above
      setDbWaking(false)
      setLoading(false)
    } catch (err: unknown) {
      if (isDbWaking(err) && retry < DB_WAKING_MAX_RETRIES) {
        setDbWaking(true)
        retryTimer.current = setTimeout(() => {
          retryTimer.current = null
          void attemptLogin(creds, retry + 1)
        }, DB_WAKING_RETRY_MS)
        return
      }
      setDbWaking(false)
      const status = (err as { response?: { status: number } })?.response?.status
      // These three are the expected outcomes of a real sign-in attempt, so each
      // gets a written message. formatApiError is the fallback only — it renders
      // the raw status and backend detail ("401 - Unauthorized — …"), which is
      // debugging output, not something to put in front of a CPA.
      if (status === 401) {
        setError('Email or password not recognized. Try again, or sign in with SSO.')
      } else if (status === 403) {
        setError("Your account isn't active yet. Contact your firm admin.")
      } else if (status === 429) {
        setError('Too many sign-in attempts. Wait 15 minutes and try again.')
      } else {
        setError(formatApiError(err))
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let google = false
      try {
        const res = await apiClient.get<{ google: boolean }>('/auth/sso-config')
        google = res?.data?.google === true
      } catch {
        google = false
      }
      if (!cancelled) {
        setGoogleEnabled(google)
        setSsoChecked(true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const handleGoogleLogin = async () => {
    try {
      const res = await apiClient.get<{ login_url: string }>('/auth/b2c/login-url')
      window.location.href = res.data.login_url
    } catch (err) {
      setError(`Google sign-in is temporarily unavailable. Please use email and password below. (${formatApiError(err)})`)
    }
  }

  // SSO-SCOPE-FIX-1 (v0.49.0): the "Sign in with Microsoft" button is gone.
  // The 2026-08-03 CIAM audit found no Microsoft-account IdP in the yansarqoe
  // tenant and none attached to either user flow, so the button's
  // domain_hint=live.com authorize pointed at an IdP that does not exist.
  // External ID external tenants offer no built-in MSA IdP and custom
  // federation was judged not worth it. Sign-in is Google + email/password
  // + email one-time passcode.

  // Parse ?error= from URL (set by B2C callback on failure)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const urlError = params.get('error')
    if (urlError === 'b2c_failed') setError('Google sign-in failed. Please try again or use email and password.')
    if (urlError === 'invalid_state') setError('Sign-in session expired. Please try again.')
  }, [location.search])

  return (
    <div className="flex min-h-screen">
      {/* ── Left brand panel (desktop only) ── */}
      <div
        className="hidden lg:flex flex-col justify-between flex-shrink-0"
        style={{ width: '280px', backgroundColor: navy, padding: '48px 40px' }}
      >
        <div>
          {/* Y mark (default brand); a white-label brand's logo, else its initial */}
          {wl?.logoUrl && !logoFailed ? (
            <img
              src={wl.logoUrl}
              alt={wl.name}
              style={{ height: '40px', width: 'auto', marginBottom: '32px' }}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div data-testid="signin-mark" style={{
              width: '48px', height: '48px', borderRadius: '10px',
              backgroundColor: gold,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '32px',
            }}>
              <span style={{ color: navy, fontWeight: 800, fontSize: '22px', fontFamily: 'Georgia, serif' }}>
                {wl ? wl.name.trim().charAt(0).toUpperCase() : 'Y'}
              </span>
            </div>
          )}
          <h2 style={{ color: '#ffffff', fontWeight: 700, fontSize: '20px', lineHeight: 1.3, marginBottom: '12px' }}>
            {PRODUCT.headline}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', lineHeight: 1.6, marginBottom: '40px' }}>
            {PRODUCT.subhead}
          </p>
          {/* Trust checkmarks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {PRODUCT.features.map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ color: gold, fontSize: '14px', fontWeight: 700, flexShrink: 0, marginTop: '1px' }}>✓</span>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <p data-testid="signin-footer" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>
          {wl ? wl.footer : '© 2026 Yansar Consulting Services LLC'}
        </p>
      </div>

      {/* ── Right form panel ── */}
      <div
        className="flex flex-col items-center justify-center flex-1 px-6 py-12"
        style={{ backgroundColor: '#f8f7f4' }}
      >
        <div style={{ width: '100%', maxWidth: '380px' }}>
          {/* Eyebrow */}
          <p style={{
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em',
            color: gold, textTransform: 'uppercase', marginBottom: '8px',
          }}>
            {wl?.name || PRODUCT.headline}
          </p>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: navy, marginBottom: '24px' }}>
            Welcome, sign in
          </h1>

          {/* Seller/Buyer notice — PRODUCT-IDENTITY-1 (R3): QoE-only. Valuation
              and Attest have no seller/buyer token-portal concept, so the
              notice must not render there. */}
          {PRODUCT_KEY === 'qoe' && (
            <Alert className="border-amber-300 bg-amber-50" style={{ marginBottom: '16px' }}>
              <AlertDescription className="text-amber-800 text-sm">
                <strong>Sellers and Buyers:</strong> use the secure link your advisor sent you.
                This login is for signers and administrators only.
              </AlertDescription>
            </Alert>
          )}

          {/* PLAT-PROD-GUARDS-1 G2: database waking — retrying automatically */}
          {dbWaking && (
            <Alert style={{ marginBottom: '16px' }}>
              <AlertDescription>{CLIENT_TEXT['signin.dbWaking']}</AlertDescription>
            </Alert>
          )}

          {/* Error alert */}
          {error && (
            <Alert variant="destructive" style={{ marginBottom: '16px' }}>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* SSO section — default view */}
          {ssoChecked && googleEnabled && !showEmailForm && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Google Sign-In button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  width: '100%', padding: '10px 16px',
                  background: '#ffffff', border: '1px solid #dadce0', borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: "'Google Sans', Roboto, Arial, sans-serif",
                  fontSize: '14px', fontWeight: 500, color: '#3c4043',
                  opacity: loading ? 0.6 : 1,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                onMouseLeave={e => (e.currentTarget.style.background = '#ffffff')}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </button>

              <div style={{ textAlign: 'center', paddingTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setShowEmailForm(true)}
                  style={{
                    fontSize: '13px', color: gold, background: 'none',
                    border: 'none', cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  Sign in with email instead ↓
                </button>
              </div>
            </div>
          )}

          {/* Email form */}
          {ssoChecked && (showEmailForm || !googleEnabled) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {googleEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0' }}>
                  <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e4e2dc' }} />
                  <span style={{ fontSize: '12px', color: '#888' }}>sign in with email</span>
                  <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e4e2dc' }} />
                </div>
              )}
              {/* noValidate: the browser's own "Please fill out this field" bubble
                  would fire first and preempt the written messages below. */}
              <form onSubmit={handleEmailLogin} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email" type="email" placeholder="you@firm.com"
                    value={email}
                    onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(null) }}
                    required autoComplete="email" autoFocus
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'email-error' : undefined}
                  />
                  {emailError && (
                    <p id="email-error" role="alert" style={{ fontSize: '12px', color: '#b91c1c' }}>
                      {emailError}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" style={{ fontSize: '12px', color: gold, textDecoration: 'underline' }}>
                      Forgot password?
                    </Link>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <Input
                      id="password" type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); if (passwordError) setPasswordError(null) }}
                      required autoComplete="current-password"
                      aria-invalid={!!passwordError}
                      aria-describedby={passwordError ? 'password-error' : undefined}
                      style={{ paddingRight: '38px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute', right: '10px', top: '50%',
                        transform: 'translateY(-50%)', background: 'none',
                        border: 'none', cursor: 'pointer', padding: 0,
                        color: '#6b7280', display: 'flex', alignItems: 'center',
                      }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {passwordError && (
                    <p id="password-error" role="alert" style={{ fontSize: '12px', color: '#b91c1c' }}>
                      {passwordError}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold"
                  style={{
                    width: '100%', padding: '10px 16px', borderRadius: '4px',
                    fontSize: '14px', cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    // White-label only; the default brand keeps .btn-gold (and its hover).
                    ...(wl ? { backgroundColor: gold, color: navy } : {}),
                  }}
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
              {googleEnabled && (
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowEmailForm(false)}
                    style={{
                      fontSize: '13px', color: '#888', background: 'none',
                      border: 'none', cursor: 'pointer', textDecoration: 'underline',
                    }}
                  >
                    ← Back to sign-in options
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>
              New here?{' '}
              <Link to="/request-access" style={{ color: gold, fontWeight: 500, textDecoration: 'underline' }}>
                Request access →
              </Link>
            </p>
            {wl?.contactEmail && (
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                Questions?{' '}
                <a href={`mailto:${wl.contactEmail}`} style={{ color: gold, textDecoration: 'underline' }}>
                  {wl.contactEmail}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
